import { useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { color as fplColor } from '@figma/fpl-tokens/tokens.stylex';
import { Box } from '@/UI/Box/Box';
import { TextSmMd, TextSmRg } from '@/UI/AppTypography/AppTypography';
import { ButtonContained } from '@/UI/Buttons/AppButton';
import { color, EL_COLORS } from '@/colors';
import { RecipeVisibilityType } from '@/enums/recipe-visibility-type';
import { ToolVisibilityType } from '@/enums/tool-visibility-type';
import { I18N_KEYS } from '@/language/keys';
import { log } from '@/logger/logger.ts';
import { Tooltip } from '@/UI/Tooltip/Tooltip';
import { FlexCenVer } from '@/UI/styles';
import { Skeleton } from '@/UI/Skeleton/Skeleton';
import { CaretIcon, CommunityIcon, CompanyIcon, FigmaMonoLogo, InfoIcon } from '@/UI/Icons';
import { FPL_ICON_COLOR_VAR } from '@/UI/Icons/fplIcon';
import { useToolListingStatus } from '@/hooks/useToolListingStatus';
import { useLinkedFigmaAccount } from '@/hooks/useLinkedFigmaAccount';
import { useLinkFigmaAccount } from '@/hooks/useLinkFigmaAccount';
import { useConfirmationDialogStore } from '@/state/confirmation-dialog.state';
import { useGlobalStore } from '@/state/global.state';
import { useWorkflowStore } from '@/state/workflow.state';
import { useFplMigrationFlag } from '@/contexts/FplMigrationContext';
import { CreditsContext } from '@/services/CreditsContext';
import { WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES } from '@/consts/analytics.consts';
import { TrackTypeEnum, useAnalytics } from '@/hooks/useAnalytics';
import { toolViewQueryKey, useSetToolVisibilityMutation, useToolViewQuery } from '@/queries/useToolView';
import PublishToCommunityAccessModal from './PublishToCommunityAccessModal';
import PublishToCommunityModal from './PublishToCommunityModal';
import {
  getToolCommunityPublishIsPublic,
  getToolOrgPublishIsShared,
  getWorkflowCommunityPublishReadiness,
  useWorkflowCommunityPublishReadiness,
  type WorkflowCommunityPublishReadiness,
} from './useWorkflowCommunityPublishReadiness';
import { useToolCommunityMetadata, useWorkflowCommunityMetadata } from './useWorkflowCommunityMetadata';
import { isPublishedToCommunity } from './workflowCommunityStatus';
import { useUnpublishWorkflowFromCommunity } from './useUnpublishWorkflowFromCommunity';
import { useUnpublishToolFromCommunity } from './useUnpublishToolFromCommunity';
import { usePublishWorkflowSource } from './publishWorkflowSource';
import {
  getCommunityPublishEligibilityBlockerMessage,
  getCommunityPublishReadinessBlockerMessage,
  hasRuntimeCommunityPublishBlocker,
} from './workflowCommunityPublishReadinessMessages';
import {
  getCommunityPublishErrorPayload,
  type WorkflowCommunityPublishMode,
  type WorkflowCommunityPublishSource,
} from './workflowCommunityPublishAnalytics';
import type { ToolView } from '@/api/toolView.api';
import type { CommunityPublishIneligibilityReason } from '@/utils/communityPublishingEligibility.utils';
import type { OrgLookupStatus, OrgPublishDestination } from '@/hooks/useOrgPublishDestinations';

const logger = log.getLogger('PublishToCommunityPanel');

const NO_ORG_DESTINATIONS: OrgPublishDestination[] = [];
const ROW_ICON_SIZE = 24;
const ROW_LABEL_SKELETON_WIDTH = 160;

interface PublishToCommunityPanelProps {
  /** Disable publishing and updates while keeping the active listing and unpublish control available. */
  isPublishingDisabled?: boolean;
  eligibilityReason?: CommunityPublishIneligibilityReason | null;
  canShowUpgradeUpsell?: boolean;
  canPublish?: boolean;
  isResolvingEligibility?: boolean;
  source?: WorkflowCommunityPublishSource;
  /**
   * When set, publish the workflow to Community as a Tool (`weave_app`) keyed by `weaveAppId`
   * (= the tool's WorkflowTool.id). Omit to publish as a workflow.
   */
  tool?: { weaveAppId: string };
  /** `'compact'` matches the Share tool modal's row shell: shorter, no trailing caret. */
  rowLayout?: 'default' | 'compact';
  /**
   * Orgs the tool can be published to. When any are given the row publishes to the first one instead
   * of Community, and the publish modal offers the rest.
   */
  orgDestinations?: OrgPublishDestination[];
  /** How the org lookup went. Unlinked lets the row link Figma; error blocks the row rather than fall back. */
  orgLookupStatus?: OrgLookupStatus;
}

const getPublishFlowNextStep = (readiness: WorkflowCommunityPublishReadiness) => {
  if (readiness.blocker === 'not_public') return 'open_access_modal';
  if (!readiness.isReady) return 'blocked';
  return 'open_publish_modal';
};

const PublishToCommunityPanel = ({
  isPublishingDisabled = false,
  eligibilityReason,
  canShowUpgradeUpsell = false,
  canPublish = false,
  isResolvingEligibility = false,
  source,
  tool,
  rowLayout = 'default',
  orgDestinations = NO_ORG_DESTINATIONS,
  orgLookupStatus = 'ready',
}: PublishToCommunityPanelProps) => {
  const { t } = useTranslation();
  const { track } = useAnalytics();
  const isFplMigrationEnabled = useFplMigrationFlag();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isAccessModalOpen, setIsAccessModalOpen] = useState(false);
  const [isUpdatingAccess, setIsUpdatingAccess] = useState(false);
  const [pendingPublishAfterLink, setPendingPublishAfterLink] = useState(false);
  const { data: connection, isLoading: isLoadingConnection } = useLinkedFigmaAccount();
  const publishWorkflow = usePublishWorkflowSource();
  const queryClient = useQueryClient();
  const { updateSnackbarData } = useGlobalStore();
  const { openUpgradeModal } = useContext(CreditsContext);
  const openConfirmationDialog = useConfirmationDialogStore((s) => s.openConfirmationDialog);
  const updateRecipeVisibility = useWorkflowStore((state) => state.updateRecipeVisibility);
  const { id: workflowId, name: workflowName } = publishWorkflow;
  const workflowVisibility = useWorkflowStore((s) => s.recipe.visibility);
  const weaveAppId = tool?.weaveAppId;

  // A Tool is published under its OWN access, so the `not_public` gate reads the tool's visibility
  // rather than the parent workflow's — a Public tool under a Private workflow must not be prompted to
  // "update access". The query is disabled (and `isLoading` false) for a workflow publish.
  const { data: toolView, isLoading: isLoadingToolView } = useToolViewQuery(weaveAppId);
  // What the access confirmation is replacing. Team is the only non-public state other than private,
  // and it reads off whichever resource the publish is for, matching the `not_public` gate above.
  const currentAccess = (
    weaveAppId ? toolView?.visibility === ToolVisibilityType.Team : workflowVisibility === RecipeVisibilityType.Team
  )
    ? 'workspace'
    : 'invited';
  // Bound to a placeholder id on the workflow path; guarded below so it is only ever invoked for a tool.
  const { mutateAsync: setToolVisibility } = useSetToolVisibilityMutation(weaveAppId ?? '');

  const {
    data: listing,
    isLoading: isLoadingListing,
    isError: isListingError,
    isOrgScopedPublishingEnabled,
  } = useToolListingStatus(weaveAppId);
  // A failed listing read (an owner without workflow edit access) falls back to Community metadata, as
  // ToolShareModal does, so a live Community listing can still be unpublished.
  const isListingUnreadable = isListingError === true && listing == null;
  const shouldFetchToolCommunityMetadata =
    !isOrgScopedPublishingEnabled || listing?.destination?.type === 'community' || isListingUnreadable;
  // A tool reads its published state under its own `weave_app_id` (separate query key); a workflow
  // reads under the workflow id. Both hooks run (rules of hooks); each is disabled when its id is undefined.
  const {
    data: workflowMetadata,
    isLoading: isLoadingWorkflowMetadata,
    isPending: isWorkflowMetadataPending,
  } = useWorkflowCommunityMetadata(tool ? undefined : workflowId);
  const {
    data: toolMetadata,
    isLoading: isLoadingToolMetadata,
    isPending: isToolMetadataPending,
  } = useToolCommunityMetadata(shouldFetchToolCommunityMetadata ? tool?.weaveAppId : undefined);
  const metadata = tool ? toolMetadata : workflowMetadata;
  const isLoadingMetadata = tool ? isLoadingToolMetadata : isLoadingWorkflowMetadata;
  // Disabled queries stay `isPending`. Only wait for metadata we intend to read with a linked
  // account; org and unpublished tools intentionally skip it, as do unlinked callers.
  const isMetadataPending =
    (tool ? shouldFetchToolCommunityMetadata && isToolMetadataPending : isWorkflowMetadataPending) &&
    connection?.connected === true;
  const isUsingToolListing = Boolean(tool) && isOrgScopedPublishingEnabled;
  const activeDestination = listing?.destination;
  const activeOrg = activeDestination?.type === 'org' ? activeDestination : null;
  // A moderated Community listing still reserves its destination until unpublish succeeds.
  // Metadata determines its visible status; the listing determines whether it can move elsewhere.
  const isPublished = activeOrg !== null || isPublishedToCommunity(metadata);
  const hasActiveListing = isUsingToolListing && !isListingUnreadable ? activeDestination != null : isPublished;
  // Updating an org listing keeps its saved destination. Eligibility still controls whether the
  // author can publish another version there; unpublish remains available when publishing is off.
  const eligibleActiveOrg = activeOrg ? orgDestinations.find((org) => org.planId === activeOrg.plan_id) : undefined;
  const isActiveOrgUnavailable = activeOrg !== null && (orgLookupStatus !== 'ready' || !eligibleActiveOrg);
  let defaultOrgDestination: OrgPublishDestination | null = null;
  if (activeOrg && eligibleActiveOrg) {
    defaultOrgDestination = eligibleActiveOrg;
  } else if (!hasActiveListing) {
    defaultOrgDestination = orgDestinations.at(0) ?? null;
  }
  const isOrgPublish = defaultOrgDestination !== null;
  // With more than one org to pick from, the row and its messages name no single org
  const hasMultipleOrgs = !hasActiveListing && orgDestinations.length > 1;
  const blockerOrgName = hasMultipleOrgs ? undefined : defaultOrgDestination?.name;
  const blockerOrgDestination = useMemo(
    () => (isOrgPublish ? { name: blockerOrgName } : undefined),
    [blockerOrgName, isOrgPublish],
  );
  let publishOrgDestinations: OrgPublishDestination[] | undefined;
  if (activeOrg) {
    publishOrgDestinations = eligibleActiveOrg ? [eligibleActiveOrg] : [];
  } else if (isOrgPublish) {
    publishOrgDestinations = orgDestinations;
  }
  // With the rollout on, tool publishing waits for its saved destination. Otherwise preserve
  // Community's existing metadata path rather than waiting on the disabled listing query.
  const isHoldingForOrgPublishState = isUsingToolListing
    ? isLoadingListing
    : orgDestinations.length > 0 && isLoadingMetadata;
  // Until the orgs are known, Community-only rules mustn't show their upsell or stop the link click.
  const isAwaitingOrgLookup = (orgLookupStatus === 'unlinked' || orgLookupStatus === 'loading') && !hasActiveListing;
  const isBlockedByOrgLookupError = orgLookupStatus === 'error' && !hasActiveListing;
  // A tool row shows a skeleton until it knows where it publishes.
  const isDestinationLoading = (() => {
    if (!tool) {
      return false;
    }
    // An unpublished tool can't pick a destination until the orgs load.
    if (orgLookupStatus === 'loading' && !hasActiveListing) {
      return true;
    }
    if (isHoldingForOrgPublishState) {
      return true;
    }
    // A Community listing still needs its metadata to show the right state.
    return shouldFetchToolCommunityMetadata && isLoadingToolMetadata;
  })();
  // Before Figma is linked we can't know where a tool publishes.
  const isAwaitingFigmaLink = Boolean(tool) && orgLookupStatus === 'unlinked' && !hasActiveListing;
  // After the orgs fail to load, the row names neither Community nor an org.
  const isOrgUnknown = isBlockedByOrgLookupError;
  // An org publish only needs the tool shared with its Weave workspace; Community needs it public.
  const getToolIsShared = isOrgPublish ? getToolOrgPublishIsShared : getToolCommunityPublishIsPublic;
  const getLatestToolIsPublic = useCallback(
    () => (weaveAppId ? getToolIsShared(queryClient.getQueryData<ToolView>(toolViewQueryKey(weaveAppId))) : undefined),
    [getToolIsShared, queryClient, weaveAppId],
  );
  const readiness = useWorkflowCommunityPublishReadiness(weaveAppId ? getToolIsShared(toolView) : undefined);
  const mode: WorkflowCommunityPublishMode = isPublished ? 'update' : 'initial';
  // An org listing counts as org even when its org is no longer offered. With several orgs and no
  // listing yet, the plan id is left for the modal's picker to set.
  const isTrackedAsOrg = activeOrg !== null || isOrgPublish;
  const trackedOrgPlanId =
    activeOrg?.plan_id ?? (orgDestinations.length === 1 ? defaultOrgDestination?.planId : undefined);
  const trackingContext = useMemo(
    () =>
      source
        ? {
            source,
            surface: tool ? ('tool' as const) : ('workflow' as const),
            mode,
            destination: isTrackedAsOrg ? ('org' as const) : ('community' as const),
            ...(trackedOrgPlanId ? { orgPlanId: trackedOrgPlanId } : {}),
            workflowId,
            ...(tool ? { toolId: tool.weaveAppId } : {}),
            resourceId: metadata?.resource_id ?? null,
          }
        : undefined,
    [isTrackedAsOrg, metadata?.resource_id, mode, source, tool, trackedOrgPlanId, workflowId],
  );
  const trackPublishEvent = useCallback(
    (eventName: string, payload: Record<string, unknown> = {}) => {
      if (!trackingContext) return;
      track(
        eventName,
        {
          ...trackingContext,
          ...payload,
        },
        TrackTypeEnum.Product,
      );
    },
    [track, trackingContext],
  );

  const getLatestReadiness = useCallback(() => {
    const latestWorkflowState = useWorkflowStore.getState();
    return getWorkflowCommunityPublishReadiness({
      isPublicOverride: getLatestToolIsPublic(),
      nodes: publishWorkflow.getLatestNodes(),
      nodeTypes: latestWorkflowState.nodeTypes,
      visibility: latestWorkflowState.recipe.visibility,
    });
  }, [getLatestToolIsPublic, publishWorkflow]);

  const continuePublishFlow = useCallback((latestReadiness: WorkflowCommunityPublishReadiness) => {
    if (latestReadiness.blocker === 'not_public') {
      setIsAccessModalOpen(true);
      return;
    }
    if (!latestReadiness.isReady) {
      return;
    }
    setIsModalOpen(true);
  }, []);

  const openPublishFlow = useCallback(() => {
    continuePublishFlow(getLatestReadiness());
  }, [continuePublishFlow, getLatestReadiness]);

  const { link, isLinking } = useLinkFigmaAccount(() => setPendingPublishAfterLink(true), trackingContext);

  useEffect(() => {
    if (!pendingPublishAfterLink || isResolvingEligibility || isHoldingForOrgPublishState || isDestinationLoading) {
      return;
    }
    setPendingPublishAfterLink(false);
    // A failed org lookup keeps the row blocked; opening Community here would bypass that.
    if (isBlockedByOrgLookupError || isActiveOrgUnavailable) return;
    if (canPublish || isOrgPublish) openPublishFlow();
  }, [
    pendingPublishAfterLink,
    isResolvingEligibility,
    isHoldingForOrgPublishState,
    isDestinationLoading,
    isBlockedByOrgLookupError,
    isActiveOrgUnavailable,
    canPublish,
    isOrgPublish,
    openPublishFlow,
  ]);

  // The banned, enterprise, org member and unpaid reasons are Community rules, so they don't block an org publish.
  const isCommunityEligibilityWaived = isOrgPublish || isAwaitingOrgLookup;
  const isBlockedByEligibility =
    !isCommunityEligibilityWaived &&
    (eligibilityReason === 'banned' || eligibilityReason === 'enterprise' || eligibilityReason === 'org_member');
  const shouldShowUpgradeUpsell =
    !isPublishingDisabled &&
    !isCommunityEligibilityWaived &&
    canShowUpgradeUpsell &&
    eligibilityReason === 'unpaid' &&
    !hasActiveListing;
  const isDisabledByPublishReadiness = hasRuntimeCommunityPublishBlocker(readiness);
  // The tool's access decides the `not_public` gate, so hold the row until it's known (no-op for a
  // workflow publish, where the tool-view query is disabled).
  const isBusy =
    isLinking ||
    isLoadingConnection ||
    isResolvingEligibility ||
    isLoadingToolView ||
    isHoldingForOrgPublishState ||
    isDestinationLoading;
  const isDisabled =
    isPublishingDisabled ||
    isActiveOrgUnavailable ||
    (isUsingToolListing && isListingError) ||
    isBusy ||
    isDisabledByPublishReadiness ||
    isBlockedByEligibility ||
    shouldShowUpgradeUpsell ||
    isBlockedByOrgLookupError;
  const blockerTooltip = getCommunityPublishReadinessBlockerMessage(readiness, t, blockerOrgDestination);
  const eligibilityTooltip = isBlockedByEligibility
    ? getCommunityPublishEligibilityBlockerMessage(eligibilityReason ?? null, t)
    : null;
  const orgLookupTooltip = isBlockedByOrgLookupError
    ? t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.LOOKUP_FAILED_TOOLTIP)
    : null;
  const tooltip = orgLookupTooltip ?? eligibilityTooltip ?? blockerTooltip;
  const isMetadataForDifferentWorkflow =
    Boolean(metadata) &&
    (tool ? toolMetadata?.weave_app_id !== tool.weaveAppId : workflowMetadata?.weave_workflow_id !== workflowId);
  const disabledReason = isBusy
    ? 'busy'
    : shouldShowUpgradeUpsell
      ? 'upgrade_required'
      : isBlockedByEligibility
        ? 'eligibility'
        : isDisabledByPublishReadiness
          ? 'readiness'
          : isBlockedByOrgLookupError
            ? 'org_lookup_failed'
            : null;
  const trackedEntrypointViewWorkflowIdRef = useRef<string | undefined>(undefined);

  useEffect(() => {
    if (
      !trackingContext ||
      trackedEntrypointViewWorkflowIdRef.current === workflowId ||
      isLoadingConnection ||
      isLoadingMetadata ||
      isLoadingToolView ||
      isHoldingForOrgPublishState ||
      isMetadataForDifferentWorkflow ||
      isMetadataPending ||
      isResolvingEligibility
    ) {
      return;
    }

    trackedEntrypointViewWorkflowIdRef.current = workflowId;
    trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.ENTRYPOINT_VIEWED, {
      disabledReason,
      eligibilityReason: eligibilityReason ?? null,
      readinessBlocker: readiness.blocker,
      figmaConnected: connection?.connected === true,
      isPublished,
    });
  }, [
    connection?.connected,
    disabledReason,
    eligibilityReason,
    isLoadingConnection,
    isLoadingMetadata,
    isLoadingToolView,
    isHoldingForOrgPublishState,
    isMetadataForDifferentWorkflow,
    isMetadataPending,
    isResolvingEligibility,
    isPublished,
    readiness.blocker,
    trackPublishEvent,
    trackingContext,
    workflowId,
  ]);

  const trackEntrypointClick = useCallback(
    (
      nextStep: 'open_publish_modal' | 'open_access_modal' | 'link_figma' | 'blocked',
      readinessSnapshot: WorkflowCommunityPublishReadiness = readiness,
    ) => {
      trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.ENTRYPOINT_CLICKED, {
        nextStep,
        disabledReason,
        eligibilityReason: eligibilityReason ?? null,
        readinessBlocker: readinessSnapshot.blocker,
        figmaConnected: connection?.connected === true,
        isPublished,
      });
    },
    [connection?.connected, disabledReason, eligibilityReason, isPublished, readiness, trackPublishEvent],
  );

  const handleConfirmAccessUpdate = useCallback(async () => {
    setIsUpdatingAccess(true);
    try {
      // A Tool publish makes the TOOL public (which also detaches it from the workflow), never the
      // parent workflow — the workflow's own access is not what gates a Community-published tool.
      if (weaveAppId) {
        await setToolVisibility(isOrgPublish ? ToolVisibilityType.Team : ToolVisibilityType.Public);
      } else {
        await updateRecipeVisibility(RecipeVisibilityType.Public);
      }
    } catch (error) {
      trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.ACCESS_UPDATE_RESULT, {
        result: 'failure',
        failureReason: 'request_failed',
        ...getCommunityPublishErrorPayload(error),
      });
      logger.error('Unexpected error updating visibility before Community publish.', error);
      updateSnackbarData({
        text: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.SNACKBAR.ACCESS_UPDATE_FAILURE),
        isOpen: true,
      });
      return;
    } finally {
      setIsUpdatingAccess(false);
    }

    // `mutateAsync` settles only after the tool-view refetch, so the cache holds the server's answer.
    const isPublicAfterUpdate = weaveAppId
      ? getLatestToolIsPublic() === true
      : useWorkflowStore.getState().recipe.visibility === RecipeVisibilityType.Public;

    if (!isPublicAfterUpdate) {
      trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.ACCESS_UPDATE_RESULT, {
        result: 'failure',
        failureReason: 'visibility_unchanged',
      });
      updateSnackbarData({
        text: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.SNACKBAR.ACCESS_UPDATE_FAILURE),
        isOpen: true,
      });
      return;
    }

    const latestReadiness = getLatestReadiness();

    setIsAccessModalOpen(false);

    if (latestReadiness.isReady) {
      trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.ACCESS_UPDATE_RESULT, {
        result: 'success',
        nextStep: 'open_publish_modal',
      });
      setIsModalOpen(true);
    } else if (hasRuntimeCommunityPublishBlocker(latestReadiness)) {
      trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.ACCESS_UPDATE_RESULT, {
        result: 'success',
        nextStep: 'blocked',
        readinessBlocker: latestReadiness.blocker,
      });
      const blockerMessage = getCommunityPublishReadinessBlockerMessage(latestReadiness, t, blockerOrgDestination);
      if (blockerMessage) {
        updateSnackbarData({
          text: blockerMessage,
          isOpen: true,
        });
      }
    } else {
      trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.ACCESS_UPDATE_RESULT, {
        result: 'success',
        nextStep: 'blocked',
        readinessBlocker: latestReadiness.blocker,
      });
    }
  }, [
    blockerOrgDestination,
    getLatestReadiness,
    getLatestToolIsPublic,
    isOrgPublish,
    setToolVisibility,
    t,
    trackPublishEvent,
    updateRecipeVisibility,
    updateSnackbarData,
    weaveAppId,
  ]);

  const handleActivate = () => {
    if (isDisabled) {
      trackEntrypointClick('blocked');
      return;
    }
    if (connection?.connected) {
      const latestReadiness = getLatestReadiness();
      trackEntrypointClick(getPublishFlowNextStep(latestReadiness), latestReadiness);
      continuePublishFlow(latestReadiness);
    } else {
      trackEntrypointClick('link_figma');
      void link();
    }
  };

  const workflowUnpublish = useUnpublishWorkflowFromCommunity(workflowId, trackingContext);
  const toolUnpublish = useUnpublishToolFromCommunity(tool?.weaveAppId ?? workflowId, trackingContext, activeOrg?.name);
  const unpublish = tool ? toolUnpublish : workflowUnpublish;

  const handleUnpublishClick = useCallback(() => {
    trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.UNPUBLISH_DIALOG_OPENED, {
      publishingStatus: metadata?.publishing_status,
    });
    const keys = I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.UNPUBLISH;
    let bodyKey: string = keys.CONFIRM_BODY;
    if (tool) {
      bodyKey = activeOrg ? keys.CONFIRM_BODY_TOOL_ORG : keys.CONFIRM_BODY_TOOL;
    }
    openConfirmationDialog({
      title: t(
        tool
          ? I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.UNPUBLISH.CONFIRM_TITLE_TOOL
          : I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.UNPUBLISH.CONFIRM_TITLE,
      ),
      message: t(bodyKey, { workflowName, toolName: toolView?.name ?? workflowName, orgName: activeOrg?.name }),
      confirmBtnText: t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.UNPUBLISH.CONFIRM_ACTION),
      destructive: true,
      width: 'sm',
      onConfirm: unpublish,
    });
  }, [
    metadata?.publishing_status,
    activeOrg,
    openConfirmationDialog,
    t,
    trackPublishEvent,
    workflowName,
    tool,
    toolView?.name,
    unpublish,
  ]);

  const handleUpgradeClick = useCallback(() => {
    trackPublishEvent(WORKFLOW_COMMUNITY_PUBLISH_EVENT_NAMES.UPGRADE_CLICKED, {
      eligibilityReason: eligibilityReason ?? null,
    });
    openUpgradeModal();
  }, [eligibilityReason, openUpgradeModal, trackPublishEvent]);

  const isCompact = rowLayout === 'compact';
  const rowLabelColor = isDisabled
    ? isFplMigrationEnabled
      ? fplColor.textDisabled
      : color.White64_T
    : isFplMigrationEnabled
      ? fplColor.text
      : color.White100;
  const rowHoverBg = isFplMigrationEnabled ? fplColor.bgHover : color.Black84;

  const rowContent = (() => {
    if (isDestinationLoading) {
      return (
        <>
          <Skeleton
            data-testid="publish-row-icon-skeleton"
            variant="rounded"
            width={ROW_ICON_SIZE}
            height={ROW_ICON_SIZE}
          />
          <TextSmRg color={rowLabelColor}>
            <Skeleton data-testid="publish-row-label-skeleton" variant="text" width={ROW_LABEL_SKELETON_WIDTH} />
          </TextSmRg>
        </>
      );
    }
    if (isAwaitingFigmaLink) {
      return (
        <>
          <FigmaMonoLogo width={ROW_ICON_SIZE} height={ROW_ICON_SIZE} />
          <TextSmRg color={rowLabelColor}>
            {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.ROW_LABEL_UNLINKED)}
          </TextSmRg>
        </>
      );
    }
    return (
      <>
        {activeOrg || defaultOrgDestination || isOrgUnknown ? (
          <CompanyIcon width={ROW_ICON_SIZE} height={ROW_ICON_SIZE} />
        ) : (
          <CommunityIcon width={ROW_ICON_SIZE} height={ROW_ICON_SIZE} />
        )}
        <TextSmRg color={rowLabelColor}>
          {getPublishRowLabel(t, {
            orgName: activeOrg?.name ?? defaultOrgDestination?.name,
            isOrgUnknown,
            isPublished,
            hasMultipleOrgs,
          })}
        </TextSmRg>
      </>
    );
  })();

  const interactiveRow = (
    <Box sx={{ flex: 1, width: '100%', ...(isCompact ? { px: 1, py: 0.25 } : { p: 1 }) }}>
      <FlexCenVer
        role="button"
        tabIndex={isDisabled ? -1 : 0}
        aria-disabled={isDisabled}
        aria-busy={isDestinationLoading}
        onClick={handleActivate}
        onKeyDown={
          isDisabled
            ? undefined
            : (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleActivate();
                }
              }
        }
        sx={{
          justifyContent: 'space-between',
          cursor: isDisabled ? 'default' : 'pointer',
          ...(isDisabled && isFplMigrationEnabled && { [FPL_ICON_COLOR_VAR]: fplColor.iconDisabled }),
          '&:hover': { background: isDisabled ? undefined : rowHoverBg },
          ...(isCompact ? { p: 0.5, minHeight: 32, borderRadius: '5px' } : { p: 1, borderRadius: 1 }),
        }}
      >
        <FlexCenVer sx={{ gap: isCompact ? 1 : 0.5, color: rowLabelColor }}>{rowContent}</FlexCenVer>
        {!isCompact && (
          <CaretIcon width={16} height={16} style={{ transform: 'rotate(-90deg)', color: rowLabelColor }} />
        )}
      </FlexCenVer>
    </Box>
  );

  const upgradeUpsell = shouldShowUpgradeUpsell ? (
    <FlexCenVer
      sx={{
        alignSelf: 'stretch',
        minHeight: 40,
        mx: 2,
        mb: 2,
        px: 1.5,
        py: 1,
        gap: 1,
        borderRadius: 1.5,
        backgroundColor: fplColor.bgSelected,
        color: fplColor.text,
      }}
    >
      <InfoIcon width={16} height={16} style={{ flexShrink: 0 }} />
      <TextSmRg color={fplColor.text} sx={{ flex: 1 }}>
        {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.UPGRADE_UPSELL.MESSAGE)}
      </TextSmRg>
      <ButtonContained
        size="small"
        mode="text"
        onClick={handleUpgradeClick}
        sx={{
          flexShrink: 0,
          height: 24,
          minWidth: 0,
          px: 1,
        }}
      >
        <TextSmMd color={fplColor.text}>
          {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.UPGRADE_UPSELL.CTA)}
        </TextSmMd>
      </ButtonContained>
    </FlexCenVer>
  ) : null;

  const panel = (
    <FlexCenVer
      sx={{
        flexDirection: shouldShowUpgradeUpsell ? 'column' : 'row',
        overflow: 'hidden',
      }}
    >
      {interactiveRow}
      {upgradeUpsell}
      {hasActiveListing && !isDestinationLoading && (
        <>
          <Box sx={{ width: '1px', height: 24, backgroundColor: EL_COLORS.BoxBorder }} />
          {/* At the default 16px the 28px button measures 60px — taller than the compact row's 36px, so
            publishing would stretch the section. Match the row's own padding instead. */}
          <FlexCenVer sx={isCompact ? { px: 1, py: 0.25 } : { p: 2 }}>
            <ButtonContained
              size="small"
              mode="outlined"
              onClick={handleUnpublishClick}
              sx={{
                color: fplColor.textDanger,
                border: `1px solid ${color.Weavy_Error_16_T}`,
                '&:hover': {
                  border: `1px solid ${color.Weavy_Error_40_T}`,
                  bgcolor: color.Weavy_Error_08_T,
                },
              }}
            >
              {t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ROW_UNPUBLISH_BUTTON)}
            </ButtonContained>
          </FlexCenVer>
        </>
      )}
    </FlexCenVer>
  );

  return (
    <>
      {!shouldShowUpgradeUpsell &&
      (isDisabledByPublishReadiness || isBlockedByEligibility || isBlockedByOrgLookupError) ? (
        <Tooltip title={tooltip} placement="top">
          <span>{panel}</span>
        </Tooltip>
      ) : (
        panel
      )}
      <PublishToCommunityAccessModal
        open={isAccessModalOpen}
        onClose={() => setIsAccessModalOpen(false)}
        onConfirm={() => void handleConfirmAccessUpdate()}
        isUpdating={isUpdatingAccess}
        currentAccess={currentAccess}
        orgName={defaultOrgDestination?.name}
      />
      <PublishToCommunityModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        source={source}
        mode={mode}
        tool={tool}
        orgDestinations={publishOrgDestinations}
      />
    </>
  );
};

function getPublishRowLabel(
  t: ReturnType<typeof useTranslation>['t'],
  {
    orgName,
    isOrgUnknown,
    isPublished,
    hasMultipleOrgs,
  }: {
    orgName?: string;
    isOrgUnknown: boolean;
    isPublished: boolean;
    hasMultipleOrgs: boolean;
  },
): string {
  if (!isPublished && hasMultipleOrgs) {
    return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.ROW_LABEL_ORG_UNKNOWN);
  }
  if (orgName) {
    const key = isPublished
      ? I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.ROW_LABEL_PUBLISHED
      : I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.ROW_LABEL;
    return t(key, { orgName });
  }
  if (isOrgUnknown) {
    return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ORG_DESTINATION.ROW_LABEL_ORG_UNKNOWN);
  }
  if (isPublished) {
    return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ROW_LABEL_PUBLISHED);
  }
  return t(I18N_KEYS.SHARE_WORKFLOW_MODAL.PUBLISH_TO_FIGMA_COMMUNITY.ROW_LABEL);
}

export default PublishToCommunityPanel;
